// packages/web/src/components/Courts/NewCourtReportDialog.tsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import AddLocationIcon from '@mui/icons-material/AddLocation';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { firebaseService } from '../../services/firebaseWeb';
import { User } from '@matchpoint/shared';

interface NewCourtReportDialogProps {
  open: boolean;
  onClose: () => void;
  user: User;
}

export const NewCourtReportDialog: React.FC<NewCourtReportDialogProps> = ({
  open,
  onClose,
  user,
}) => {
  const [courtName, setCourtName] = useState('');
  const [courtAddress, setCourtAddress] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!courtName.trim() || !courtAddress.trim()) {
      setError('Please provide both court name and address.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await firebaseService.initializeApp();
      const db = firebaseService.getFirestore();

      const reportData = {
        courtId: 'new-court-request',
        courtName: courtName.trim(),
        reportedBy: user.uid,
        reporterName: user.name,
        reporterEmail: user.email,
        message: `NEW COURT REQUEST:\n\nCourt Name: ${courtName.trim()}\nAddress: ${courtAddress.trim()}\n\nAdditional Information:\n${additionalInfo.trim() || 'None provided'}`,
        status: 'pending',
        requestType: 'new_court',
        courtAddress: courtAddress.trim(),
        additionalInfo: additionalInfo.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await addDoc(collection(db, 'courtReports'), reportData);
      
      setSuccess(true);
      setCourtName('');
      setCourtAddress('');
      setAdditionalInfo('');
      
      // Auto close after success
      setTimeout(() => {
        handleClose();
      }, 2000);
      
    } catch (err) {
      console.error('Error submitting new court request:', err);
      setError('Failed to submit court request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setCourtName('');
    setCourtAddress('');
    setAdditionalInfo('');
    setError(null);
    setSuccess(false);
    setSubmitting(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AddLocationIcon color="primary" />
        Request New Court Addition
      </DialogTitle>
      
      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Thank you for your court suggestion! We'll review your request and add the court if it meets our criteria.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Can't find a tennis court in our database? Help us expand our coverage by suggesting a new court to add.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Court Name *"
              placeholder="e.g., Central Park Tennis Center"
              value={courtName}
              onChange={(e) => setCourtName(e.target.value)}
              disabled={submitting}
              variant="outlined"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Court Address *"
              placeholder="e.g., 123 Tennis Ave, New York, NY 10001"
              value={courtAddress}
              onChange={(e) => setCourtAddress(e.target.value)}
              disabled={submitting}
              variant="outlined"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Additional Information (Optional)"
              placeholder="Any additional details like facility type, number of courts, amenities, contact info, website, etc."
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
              disabled={submitting}
              variant="outlined"
            />

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              We'll research and verify the court information before adding it to our database.
            </Typography>
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {success ? 'Close' : 'Cancel'}
        </Button>
        {!success && (
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={submitting || !courtName.trim() || !courtAddress.trim()}
          >
            {submitting ? <CircularProgress size={24} /> : 'Submit Request'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};